import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import styles from "./Auth.module.css";

/**
 * TwoFactorLogin Component
 * Handles TOTP (Authenticator App) verification during the login flow.
 */
const TwoFactorLogin = () => {
  const { verify2FALogin, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Retrieve email from navigation state
  const email = location.state?.email;

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const otpRefs = useRef([]);

  // Redirect back to login if email is missing (direct access attempt)
  useEffect(() => {
    if (!email) {
      toast.error("Session expired or invalid. Please login again.");
      navigate("/login");
    }
  }, [email, navigate]);

  const handleChange = (e, index) => {
    const val = e.target.value.slice(-1);
    if (isNaN(val)) return;

    const newOtp = [...otp];
    newOtp[index] = val;
    setOtp(newOtp);

    // Auto-focus next input
    if (val && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    const token = otp.join("");
    if (token.length !== 6) {
      toast.error("Please enter the 6-digit code from your app");
      return;
    }

    try {
      const res = await verify2FALogin(email, token);
      if (res.ok) {
        navigate("/user-dashboard");
      }
    } catch (err) {
      // Error is handled by AuthContext toast
      setOtp(["", "", "", "", "", ""]);
      otpRefs.current[0]?.focus();
    }
  };

  return (
    <div className={styles.authWrapper}>
      <div className={styles.authCard}>
        <div className={styles.authHeader}>
          <h1 className={styles.authTitle}>Two-Factor Authentication</h1>
          <p className={styles.subtitle}>
            Enter the 6-digit code from your authenticator app for <strong>{email}</strong>
          </p>
        </div>

        <form onSubmit={handleSubmit} className={styles.authForm}>
          <div className={styles.otpContainer}>
            <div className={styles.otpInputs}>
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (otpRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(e, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  className={styles.otpInput}
                  disabled={loading}
                  autoFocus={index === 0}
                />
              ))}
            </div>
          </div>

          <button
            type="submit"
            className={`${styles.authButton} ${loading ? styles.loading : ""}`}
            disabled={loading || otp.some((d) => d === "")}
          >
            {loading ? "Verifying..." : "Verify & Login"}
          </button>

          <button
            type="button"
            className={styles.backBtn}
            onClick={() => navigate("/login")}
          >
            Back to Login
          </button>
        </form>

        <div className={styles.authFooter}>
          <p className={styles.helpText}>
            Lost access to your device? Contact support to recover your account.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorLogin;
